import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { axialToPixel, hexPolygonPoints, toPointsAttr, colorForCell, computeBounds, STATUS_COLORS, IMPERIAL_COLORS, getStatusColors, normalizeStatus, axialNeighbor, AXIAL_DIR_TO_EDGE, hexEdgeEndpoints, edgesToSvgPathD } from './hexUtils'
import './HexMap.css'

/**
 * HexMap
 * - Render por capas y compatibilidad legacy.
 * - Pan & Zoom (wheel + drag).
 * - Tooltip básico y accesibilidad por foco/teclado.
 *
 * Nuevas props (opcionales):
 * - toolMode: 'move' | 'paint' | 'erase' (default: 'paint' para compatibilidad).
 * - hanCells: Array de celdas de la capa Han. Si no se pasan hanCells/empireCells pero sí legacy `cells`, se interpreta como Han.
 * - empireCells: Array de celdas de la capa Imperio (tiene prioridad de fill sobre Han).
 * - pendingHanCells: Array de celdas Han pendientes (overlay de fill con alpha ~0.6).
 * - pendingEmpireCells: Array de celdas Imperio pendientes (overlay de fill con alpha ~0.6).
 * - pendingClanAssignments: Array<{ q, r, clanId, territorySlug? }> para previsualizar stroke de clan (solo borde).
 * - pendingDeletes: Array<{ layer: 'han'|'empire', q, r, territorySlug? }> para overlay de borrado (stroke rojo punteado y sin fill).
 *
 * Compatibilidad legacy:
 * - Props legacy: cells, editable, pendingCells, onBrushStart/Over/End, onHoverCell se mantienen.
 * - Si toolMode no se pasa, el comportamiento por defecto sigue siendo pintado cuando editable=true.
 *
 * Interacción Pan vs Pintado:
 * - toolMode === 'move': permitir pan aunque editable=true; NO iniciar pintado en handleSvgMouseDown.
 * - toolMode !== 'move' y editable=true: lógica de pintado drag legacy se mantiene.
 *
 * Esquema de render por capas:
 * 1) Fill Imperio (prioridad) — color imperial; sellado de juntas = stroke del fill.
 * 2) Fill Han (si no hay Imperio) — color Han; sellado de juntas = stroke del fill.
 * 3) Overlay opcional de estado (tinte suave Paz/Guerra/…) si showStatusTint.
 * 4) Path continuo de frontera Han (Han↔Han / exterior).
 * 5) Path continuo de clan (malla interna + frontera, mismo color).
 * 6) Overlays:
 *    - pendingCells (legacy) como hoy.
 *    - pendingEmpireCells / pendingHanCells: overlay de fill con alpha ~0.6.
 *    - pendingClanAssignments: stroke-only con color de clan pendiente.
 *    - pendingDeletes: fill=none y stroke rojo punteado. Si layer==='han', no debe verse stroke por clan (se fuerza vista sin ese stroke durante la previsualización).
 * 7) Grid opcional (aristas alineadas con AXIAL_DIR_TO_EDGE).
 */

// Hash de color determinista para clan (stroke)
function hashClanColor(clanId, s = 62, l = 46) {
  if (clanId == null) return '#607D8B'
  const str = String(clanId)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash |= 0
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue} ${s}% ${l}%)`
}

function toKey(q, r) {
  return `${Number(q)},${Number(r)}`
}

function withAlpha(hex, aa = '99') {
  // si ya tiene alpha, retornar tal cual
  if (typeof hex === 'string' && /^#([0-9a-f]{8})$/i.test(hex)) return hex
  if (typeof hex === 'string' && /^#([0-9a-f]{6})$/i.test(hex)) return `${hex}${aa}`
  return hex || '#9E9E9E22'
}

// Quita el canal alpha de un color hex #RRGGBBAA → #RRGGBB
function stripAlphaHex(hex) {
  if (typeof hex !== 'string') return hex
  const m8 = hex.trim().match(/^#([0-9a-f]{6})([0-9a-f]{2})$/i)
  if (m8) return `#${m8[1]}`
  const m6 = hex.trim().match(/^#([0-9a-f]{6})$/i)
  if (m6) return hex
  return hex
}

/** Luminancia relativa 0..1 (sRGB) para decidir contraste suave del grid. */
function relativeLuminance(hex) {
  const h = stripAlphaHex(hex)
  const m = typeof h === 'string' && h.trim().match(/^#([0-9a-f]{6})$/i)
  if (!m) return 0.5
  const toLin = (n) => {
    const c = n / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const r = toLin(parseInt(m[1].slice(0, 2), 16))
  const g = toLin(parseInt(m[1].slice(2, 4), 16))
  const b = toLin(parseInt(m[1].slice(4, 6), 16))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Stroke del grid: oscuro suave sobre fills claros, claro suave sobre oscuros. */
function gridStrokeForBackground(fillHex) {
  const L = relativeLuminance(fillHex)
  // Contraste moderado (alpha ~0.35–0.4), no agresivo
  return L >= 0.52 ? '#1c1c2259' : '#ffffff66'
}

// Color imperial a partir de datos de celda (slug/nombre). Heurística simple y estable.
function pickImperialBase(cell) {
  const norm = (s) => (s && s.normalize ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s)
  const toKeyStr = (s) => String(s || '').toLowerCase().trim()
  const candidates = [
    cell?.empireSlug,
    cell?.empireName,
    cell?.shogunateSlug,
    cell?.shogunateName,
    cell?.territoryEmpireSlug,
    cell?.territoryEmpireName,
  ].filter(Boolean)

  for (const c of candidates) {
    const k = toKeyStr(norm(c))
    if (IMPERIAL_COLORS[k]) return IMPERIAL_COLORS[k]
  }

  // heurística por tokens
  const joined = toKeyStr(norm(candidates.join(' ')))
  if (joined.includes('fuego')) return IMPERIAL_COLORS['fuego']
  if (joined.includes('masadora')) return IMPERIAL_COLORS['masadora']
  if (joined.includes('meshokan')) return IMPERIAL_COLORS['meshokan']
  if (joined.includes('jiyu')) return IMPERIAL_COLORS['jiyu']

  return '#546E7A' // fallback sobrio
}

export default function HexMap({
  // Legacy: una sola capa (Han)
  cells = [],

  // Nuevo API por capas
  hanCells = undefined,
  empireCells = [],
  clanCells = [],
  hanPalette = null,
  clanDict = null,
  showHan = true,
  showEmpire = true,
  activeLayer = null,

  // Fondo global (imagen) — se renderiza detrás de las capas y se mueve/zoomea con el mapa
  backgroundImageUrl = null,
  backgroundOpacity = 0.9,
  // Tamaño mínimo del lienzo de fondo (solo aplica si hay backgroundImageUrl)
  backgroundMinWidth = 1000,
  backgroundMinHeight = 1000,

  // Foco de Imperio y centrado/zoom en una celda concreta (mini-mapas, vistas de detalle)
  empireFocusSlug = null,
  empireDimOpacity = 0.25, // opacidad para imperios no focalizados
  // Foco de Han (para atenuar Han vecinos cuando se muestran todos los Han)
  hanFocusSlug = null,
  hanDimOpacity = 0.35,    // opacidad para Han no focalizados
  focusHex = null,         // { q, r } para centrar vista
  focusZoom = null,        // número (0.25 .. 4) para zoom inicial sobre focusHex

  // Auto-fit a un subconjunto de celdas (p.ej., contorno de un Han)
  fitToCells = null,
  // Factor multiplicativo para el auto-fit (para acercar más el encuadre)
  fitScaleFactor = 1,
  // Centro preferido para el auto-fit (en axial), para sesgar el encuadre hacia una celda (p.ej., q,r de la aventura)
  fitCenterHex = null,     // { q, r } | null
  // Intensidad del sesgo hacia fitCenterHex (0 = solo bbox, 1 = solo fitCenterHex)
  fitCenterBias = 0.5,

  // Herramienta
  toolMode = 'paint',

  hexSize = 18,
  onCellClick = () => { },
  initialCenter = null,
  showGrid = false,
  // Tinte suave por estado de celda (Paz / Neutral / Guerra…). Off por defecto (mapa global = toggle).
  showStatusTint = false,

  // Estilo del contenedor (opcional). Si se pasa height, sobrescribe el CSS por inline style.
  containerStyle = undefined,

  // edición legacy (aplica a Han)
  editable = false,
  pendingCells = [],
  onBrushStart,
  onBrushOver,
  onBrushEnd,
  onHoverCell,

  // nuevas pendientes
  pendingHanCells = [],
  pendingEmpireCells = [],
  pendingClanAssignments = [],
  pendingDeletes = [],

  // Resaltado de una celda concreta (p.ej., ubicación de aventura)
  highlightHex = null,            // { q, r }
  highlightColor = '#FFD54F',     // ámbar suave
  highlightStrokeWidth = 2,       // px

  // Marcadores de iconos (ej: capital)
  markers = [],
  // Interacción de marcadores (ej: click/hover en capital)
  onMarkerClick = () => { },
  onMarkerHover = () => { },
  onMarkerLeave = () => { },
}) {
  const containerRef = useRef(null)
  const svgRef = useRef(null)

  // Observador de tamaño del contenedor para recalcular fit cuando cambie (ej. al mostrarse en sidebar)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    // Inicial
    setContainerSize({ width: el.clientWidth, height: el.clientHeight })
    // ResizeObserver si está disponible
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0]
        const cr = entry?.contentRect
        const w = Math.round((cr?.width ?? el.clientWidth) || 0)
        const h = Math.round((cr?.height ?? el.clientHeight) || 0)
        setContainerSize({ width: w, height: h })
      })
      ro.observe(el)
      return () => ro.disconnect()
    } else {
      // Fallback: listener de window resize
      const onWinResize = () => {
        if (!containerRef.current) return
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }
      window.addEventListener('resize', onWinResize)
      return () => window.removeEventListener('resize', onWinResize)
    }
  }, [])

  // Transformaciones para pan/zoom
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const panOrigin = useRef({ x: 0, y: 0 })
  const panStart = useRef({ x: 0, y: 0 })

  // Estado de pintado legacy
  const [isPainting, setIsPainting] = useState(false)

  const [hoverCell, setHoverCell] = useState(null)
  const [hoverMarker, setHoverMarker] = useState(null)

  // Selección de fuentes por capa (compatibilidad)
  const effectiveHanCells = useMemo(
    () => (Array.isArray(hanCells) ? hanCells : (Array.isArray(cells) ? cells : [])),
    [hanCells, cells]
  )
  const effectiveEmpireCells = useMemo(
    () => (Array.isArray(empireCells) ? empireCells : []),
    [empireCells]
  )

  // Bounds de las celdas para centrar inicial (ambas capas)
  const allCellsForBounds = useMemo(
    () => [...effectiveEmpireCells, ...effectiveHanCells],
    [effectiveEmpireCells, effectiveHanCells]
  )
  const bounds = useMemo(() => computeBounds(allCellsForBounds, hexSize), [allCellsForBounds, hexSize])

  // Ajuste de bounds para fondo: expandir a un mínimo (p.ej. 1000x1000) solo si hay background
  const hasBackground = useMemo(() => {
    return typeof backgroundImageUrl === 'string' && backgroundImageUrl.trim().length > 0
  }, [backgroundImageUrl])

  const renderBounds = useMemo(() => {
    if (!hasBackground) return bounds
    const bW = bounds.maxX - bounds.minX
    const bH = bounds.maxY - bounds.minY
    const minW = Math.max(0, Number(backgroundMinWidth) || 0)
    const minH = Math.max(0, Number(backgroundMinHeight) || 0)
    const extraW = Math.max(0, minW - bW)
    const extraH = Math.max(0, minH - bH)
    if (extraW <= 0 && extraH <= 0) return bounds
    return {
      minX: bounds.minX - extraW / 2,
      maxX: bounds.maxX + extraW / 2,
      minY: bounds.minY - extraH / 2,
      maxY: bounds.maxY + extraH / 2,
    }
  }, [hasBackground, bounds, backgroundMinWidth, backgroundMinHeight])

  const renderBoundsKey = useMemo(
    () => `${renderBounds.minX}|${renderBounds.minY}|${renderBounds.maxX}|${renderBounds.maxY}|${hexSize}`,
    [renderBounds, hexSize]
  )

  // Fit inicial
  const didFitRef = useRef(false)
  useEffect(() => {
    if (didFitRef.current) return
    if (!containerRef.current) return

    // Si hay foco explícito (focusHex válido), omitir el auto-fit inicial para no
    // sobreescribir el centrado/zoom del foco que se aplica en el efecto de focus.
    const hasFocus =
      focusHex &&
      Number.isFinite(Number(focusHex?.q)) &&
      Number.isFinite(Number(focusHex?.r))
    if (hasFocus) {
      didFitRef.current = true
      return
    }

    // Si ya nos están pasando fitToCells (auto-fit dirigido), NO hagas fit inicial global.
    if (Array.isArray(fitToCells) && fitToCells.length > 0) {
      didFitRef.current = true
      return
    }

    const { clientWidth, clientHeight } = containerRef.current
    const cx = clientWidth / 2
    const cy = clientHeight / 2

    if (initialCenter && Number.isFinite(initialCenter.x) && Number.isFinite(initialCenter.y)) {
      setTranslate({ x: initialCenter.x, y: initialCenter.y })
      didFitRef.current = true
      return
    }

    // Usar bounds expandidos si hay background para que el encuadre contemple 1000x1000 (u otro mínimo)
    const fitB = renderBounds
    const width = fitB.maxX - fitB.minX
    const height = fitB.maxY - fitB.minY
    if (!Number.isFinite(width) || !Number.isFinite(height) || (width <= 0 && height <= 0)) {
      setScale(1)
      setTranslate({ x: cx, y: cy })
      didFitRef.current = true
      return
    }

    const pad = 24
    const sx = (clientWidth - pad) / Math.max(1, width)
    const sy = (clientHeight - pad) / Math.max(1, height)
    const s = Math.max(0.25, Math.min(2, Math.min(sx, sy)))
    const worldCenterX = fitB.minX + width / 2
    const worldCenterY = fitB.minY + height / 2
    const tx = cx - worldCenterX * s
    const ty = cy - worldCenterY * s

    setScale(s)
    setTranslate({ x: tx, y: ty })
    didFitRef.current = true
  }, [renderBoundsKey, focusHex, fitToCells])

  // Enfoque explícito en una celda (q,r) con zoom específico
  const didFocusRef = useRef(false)
  const focusKey = useMemo(() => {
    const valid =
      focusHex &&
      Number.isFinite(Number(focusHex?.q)) &&
      Number.isFinite(Number(focusHex?.r))
    return valid ? `${Number(focusHex.q)},${Number(focusHex.r)}|${Number(focusZoom) || 0}|${hexSize}` : null
  }, [focusHex, focusZoom, hexSize])

  useEffect(() => {
    if (!focusKey) return
    if (!containerRef.current) return
    // Aplicar siempre que cambie la clave de foco (permite re-centrar)
    const { clientWidth, clientHeight } = containerRef.current
    const cx = clientWidth / 2
    const cy = clientHeight / 2
    const q = Number(focusHex.q)
    const r = Number(focusHex.r)
    const { x, y } = axialToPixel(q, r, hexSize)
    const desiredScale = Math.max(0.25, Math.min(4, Number(focusZoom) || 2))
    const tx = cx - x * desiredScale
    const ty = cy - y * desiredScale
    try {
      console.log?.('[HexMap] focus-center', {
        focusKey,
        q, r,
        desiredScale,
        translate: { x: tx, y: ty }
      })
    } catch { }
    setScale(desiredScale)
    setTranslate({ x: tx, y: ty })
    didFocusRef.current = true
  }, [focusKey])

  // Auto-ajuste de vista a un conjunto de celdas (fitToCells) si se provee y no hay focusHex.
  // Se programa en dos frames para asegurar que el contenedor ya tiene layout válido (sidebar/render diferidos).
  useEffect(() => {
    if (!Array.isArray(fitToCells) || fitToCells.length === 0) return
    if (!containerRef.current) return

    const hasFocus =
      focusHex &&
      Number.isFinite(Number(focusHex?.q)) &&
      Number.isFinite(Number(focusHex?.r))
    if (hasFocus) return

    let raf1 = 0
    let raf2 = 0
    const run = () => {
      const el = containerRef.current
      if (!el) return
      const { clientWidth, clientHeight } = el
      if (clientWidth <= 0 || clientHeight <= 0) return

      const cx = clientWidth / 2
      const cy = clientHeight / 2

      const fbounds = computeBounds(fitToCells, hexSize)
      const width = fbounds.maxX - fbounds.minX
      const height = fbounds.maxY - fbounds.minY
      if (!Number.isFinite(width) || !Number.isFinite(height) || (width <= 0 && height <= 0)) return

      const pad = 28
      const sx = (clientWidth - pad) / Math.max(1, width)
      const sy = (clientHeight - pad) / Math.max(1, height)
      const base = Math.min(sx, sy)
      const factor = Number.isFinite(Number(fitScaleFactor)) ? Math.max(0.5, Math.min(3, Number(fitScaleFactor))) : 1
      const s = Math.max(0.25, Math.min(4, base * factor))
      // Centro por bbox
      const bboxCenterX = fbounds.minX + width / 2
      const bboxCenterY = fbounds.minY + height / 2

      // Centro preferido (si viene fitCenterHex) en coords mundo, con sesgo
      let targetCenterX = bboxCenterX
      let targetCenterY = bboxCenterY
      const bias = Number.isFinite(Number(fitCenterBias)) ? Math.max(0, Math.min(1, Number(fitCenterBias))) : 0.5
      if (
        fitCenterHex &&
        Number.isFinite(Number(fitCenterHex?.q)) &&
        Number.isFinite(Number(fitCenterHex?.r)) &&
        bias > 0
      ) {
        const q = Number(fitCenterHex.q)
        const r = Number(fitCenterHex.r)
        const pref = axialToPixel(q, r, hexSize)
        targetCenterX = bboxCenterX * (1 - bias) + pref.x * bias
        targetCenterY = bboxCenterY * (1 - bias) + pref.y * bias
      }

      const tx = cx - targetCenterX * s
      const ty = cy - targetCenterY * s

      try {
        console.log?.('[HexMap] fitToCells', {
          count: Array.isArray(fitToCells) ? fitToCells.length : 0,
          container: { w: clientWidth, h: clientHeight },
          fbounds: { minX: fbounds.minX, minY: fbounds.minY, maxX: fbounds.maxX, maxY: fbounds.maxY },
          width,
          height,
          pad: 28,
          baseScale: base,
          fitScaleFactor: factor,
          fitCenterBias: bias,
          centers: {
            bbox: { x: bboxCenterX, y: bboxCenterY },
            target: { x: targetCenterX, y: targetCenterY },
          },
          scale: s,
          translate: { x: tx, y: ty },
        })
      } catch { }
      setScale(s)
      setTranslate({ x: tx, y: ty })
      didFitRef.current = true
    }

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(run)
    })
    return () => {
      if (raf1) cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
    }
  }, [fitToCells, hexSize, containerSize.width, containerSize.height, focusHex, fitScaleFactor, fitCenterHex, fitCenterBias])

  // Zoom centrado en cursor
  const onWheel = useCallback(
    (e) => {
      const delta = -e.deltaY
      const factor = Math.exp(delta * 0.0015)
      const nextScale = Math.min(4, Math.max(0.25, scale * factor))

      const rect = svgRef.current?.getBoundingClientRect()
      if (!rect) {
        setScale(nextScale)
        return
      }
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top

      const worldXBefore = (cx - translate.x) / scale
      const worldYBefore = (cy - translate.y) / scale
      const newTranslateX = cx - worldXBefore * nextScale
      const newTranslateY = cy - worldYBefore * nextScale

      setScale(nextScale)
      setTranslate({ x: newTranslateX, y: newTranslateY })
    },
    [scale, translate]
  )

  // Pan con mouse (permitir incluso si editable cuando toolMode==='move')
  const onMouseDown = (e) => {
    if (e.button !== 0) return
    if (editable && toolMode !== 'move') return
    setIsPanning(true)
    panOrigin.current = { x: e.clientX, y: e.clientY }
    panStart.current = { ...translate }
  }
  const onMouseMove = (e) => {
    if (!isPanning) return
    const dx = e.clientX - panOrigin.current.x
    const dy = e.clientY - panOrigin.current.y
    setTranslate({ x: panStart.current.x + dx, y: panStart.current.y + dy })
  }
  const endPan = () => setIsPanning(false)

  // === Pintado por coordenadas del puntero (no requiere hex base) ===
  const clientToWorld = useCallback(
    (e) => {
      const rect = svgRef.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const worldX = (cx - translate.x) / scale
      const worldY = (cy - translate.y) / scale
      return { x: worldX, y: worldY }
    },
    [translate, scale]
  )

  const axialRound = (qf, rf) => {
    let x = qf
    let z = rf
    let y = -x - z
    let rx = Math.round(x)
    let ry = Math.round(y)
    let rz = Math.round(z)
    const x_diff = Math.abs(rx - x)
    const y_diff = Math.abs(ry - y)
    const z_diff = Math.abs(rz - z)
    if (x_diff > y_diff && x_diff > z_diff) {
      rx = -ry - rz
    } else if (y_diff > z_diff) {
      ry = -rx - rz
    } else {
      rz = -rx - ry
    }
    return { q: rx, r: rz }
  }

  const pixelToAxialPointy = (x, y, size) => {
    const q = (Math.sqrt(3) / 3 * x - 1 / 3 * y) / size
    const r = (2 / 3 * y) / size
    return axialRound(q, r)
  }

  const startPaintAtPointer = (e) => {
    if (!editable || toolMode === 'move' || e.button !== 0) return
    if (e && e.cancelable) e.preventDefault()
    e?.stopPropagation?.()
    const { x, y } = clientToWorld(e)
    const { q, r } = pixelToAxialPointy(x, y, hexSize)
    if (!isPainting) setIsPainting(true)
    onBrushStart?.({ q, r })
    onHoverCell?.({ q, r })
  }

  const overPointer = (e) => {
    if (!editable || toolMode === 'move') return
    if (isPainting) {
      const { x, y } = clientToWorld(e)
      const { q, r } = pixelToAxialPointy(x, y, hexSize)
      onBrushOver?.({ q, r })
      onHoverCell?.({ q, r })
    }
  }

  const handleSvgMouseDown = (e) => {
    if (toolMode === 'move') return onMouseDown(e)
    if (editable) return startPaintAtPointer(e)
    return onMouseDown(e)
  }

  const handleSvgMouseMove = (e) => {
    if (toolMode === 'move') return onMouseMove(e)
    if (editable) return overPointer(e)
    return onMouseMove(e)
  }

  // Keyboard pan/zoom
  const onKeyDown = (e) => {
    const step = 20
    if (e.key === '+' || e.key === '=') {
      setScale((s) => Math.min(4, s * 1.1))
    } else if (e.key === '-' || e.key === '_') {
      setScale((s) => Math.max(0.25, s / 1.1))
    } else if (e.key === 'ArrowLeft') {
      setTranslate((t) => ({ ...t, x: t.x + step }))
    } else if (e.key === 'ArrowRight') {
      setTranslate((t) => ({ ...t, x: t.x - step }))
    } else if (e.key === 'ArrowUp') {
      setTranslate((t) => ({ ...t, y: t.y + step }))
    } else if (e.key === 'ArrowDown') {
      setTranslate((t) => ({ ...t, y: t.y - step }))
    }
  }

  // Polígonos base Han. Dedupe por (q,r): duplicados dibujaban cada arista de clan 2×.
  const hanHexes = useMemo(() => {
    const byKey = new Map()
    for (const c of effectiveHanCells) {
      const q = Number(c.q)
      const r = Number(c.r)
      if (!Number.isFinite(q) || !Number.isFinite(r)) continue
      const { x, y } = axialToPixel(q, r, hexSize)
      const points = hexPolygonPoints(x, y, hexSize)
      const pts = toPointsAttr(points)
      byKey.set(toKey(q, r), { c, q, r, x, y, pts })
    }
    return Array.from(byKey.values())
  }, [effectiveHanCells, hexSize])

  // Celdas del Han en foco (para atenuar imperio/borde fuera de ese territorio)
  const focusedHanKeySet = useMemo(() => {
    const focusSlug =
      typeof hanFocusSlug === 'string' && hanFocusSlug.trim()
        ? hanFocusSlug.trim().toLowerCase()
        : null
    if (!focusSlug) return null
    const s = new Set()
    for (const { c, q, r } of hanHexes) {
      const thisSlug = String(
        c?.hanSlug || c?.territorySlug || c?.hanName || c?.territoryName || ''
      ).toLowerCase()
      if (thisSlug === focusSlug) s.add(toKey(q, r))
    }
    return s.size > 0 ? s : null
  }, [hanHexes, hanFocusSlug])

  // Polígonos base Imperio
  const empireHexes = useMemo(() => {
    return effectiveEmpireCells.map((c) => {
      const q = Number(c.q)
      const r = Number(c.r)
      const { x, y } = axialToPixel(q, r, hexSize)
      const points = hexPolygonPoints(x, y, hexSize)
      const pts = toPointsAttr(points)
      return { c, q, r, x, y, pts }
    })
  }, [effectiveEmpireCells, hexSize])

  // Índice rápido para obtener el nombre del imperio dueño de un (q,r)
  const empireLabelByKey = useMemo(() => {
    const m = new Map()
    for (const { c, q, r } of empireHexes) {
      const label =
        c?.empireName ||
        c?.empireSlug ||
        c?.shogunateName ||
        c?.shogunateSlug ||
        'Imperio'
      m.set(toKey(q, r), label)
    }
    return m
  }, [empireHexes])

  // Slug de imperio por celda (para atenuar Hans fuera del imperio en foco)
  const empireSlugByKey = useMemo(() => {
    const m = new Map()
    for (const { c, q, r } of empireHexes) {
      const s = String(
        c?.empireSlug || c?.shogunateSlug || c?.territoryEmpireSlug || ''
      ).toLowerCase()
      if (s) m.set(toKey(q, r), s)
    }
    return m
  }, [empireHexes])

  // Set de claves imperiales para prioridad de fill (respetando showEmpire)
  const empireKeySet = useMemo(() => {
    if (!showEmpire) return new Set()
    const s = new Set()
    for (const h of empireHexes) s.add(toKey(h.q, h.r))
    return s
  }, [empireHexes, showEmpire])

  // Índice de capa Clan base (solo visual; ignora clanId en Han)
  const clanIndex = useMemo(() => {
    const m = new Map()
    const list = Array.isArray(clanCells) ? clanCells : []
    for (const c of list) {
      const key = toKey(Number(c.q), Number(c.r))
      m.set(key, {
        clanId: c?.clanId ?? null,
        clanColor: c?.clanColor ?? null,
      })
    }
    return m
  }, [clanCells])

  // Overlays legacy (se mantienen tal cual)
  const overlayHexes = useMemo(() => {
    const list = Array.isArray(pendingCells) ? pendingCells : []
    return list.map((c) => {
      const q = Number(c.q)
      const r = Number(c.r)
      const { x, y } = axialToPixel(q, r, hexSize)
      const points = hexPolygonPoints(x, y, hexSize)
      const pts = toPointsAttr(points)
      const colors = colorForCell(c)
      return { c, q, r, x, y, pts, colors }
    })
  }, [pendingCells, hexSize])

  const pendingIndex = useMemo(() => {
    const m = new Map()
    const list = Array.isArray(pendingCells) ? pendingCells : []
    for (const c of list) m.set(toKey(c.q, c.r), c)
    return m
  }, [pendingCells])

  // Overlays nuevos
  const pendingEmpireOverlay = useMemo(() => {
    const list = Array.isArray(pendingEmpireCells) ? pendingEmpireCells : []
    return list.map((c) => {
      const q = Number(c.q)
      const r = Number(c.r)
      const { x, y } = axialToPixel(q, r, hexSize)
      const pts = toPointsAttr(hexPolygonPoints(x, y, hexSize))
      return { c, q, r, pts }
    })
  }, [pendingEmpireCells, hexSize])

  const pendingHanOverlay = useMemo(() => {
    const list = Array.isArray(pendingHanCells) ? pendingHanCells : []
    return list.map((c) => {
      const q = Number(c.q)
      const r = Number(c.r)
      const { x, y } = axialToPixel(q, r, hexSize)
      const pts = toPointsAttr(hexPolygonPoints(x, y, hexSize))
      return { c, q, r, pts }
    })
  }, [pendingHanCells, hexSize])

  const pendingClanMap = useMemo(() => {
    const m = new Map()
    const list = Array.isArray(pendingClanAssignments) ? pendingClanAssignments : []
    for (const it of list) m.set(toKey(it.q, it.r), it)
    return m
  }, [pendingClanAssignments])

  const hanDeleteSet = useMemo(() => {
    const s = new Set()
    const list = Array.isArray(pendingDeletes) ? pendingDeletes : []
    for (const it of list) if (it.layer === 'han') s.add(toKey(it.q, it.r))
    return s
  }, [pendingDeletes])

  /**
   * Dueño del borde visual por celda (clan:id o han:slug).
   * Usado para dibujar solo aristas de frontera (mismo dueño = sin trazo interno).
   */
  const borderOwnerByKey = useMemo(() => {
    const m = new Map()
    for (const { c, q, r } of hanHexes) {
      const key = toKey(q, r)
      if (hanDeleteSet.has(key)) continue
      const pendingAssign = pendingClanMap.get(key)
      const hasPendingClear =
        !!(pendingAssign && Object.prototype.hasOwnProperty.call(pendingAssign, 'clanId') && pendingAssign.clanId === null)
      if (hasPendingClear) continue
      const baseClan = clanIndex.get(key)
      const effClanId = pendingAssign?.clanId != null ? pendingAssign.clanId : (baseClan?.clanId ?? null)
      if (effClanId != null) {
        m.set(key, `clan:${effClanId}`)
      }
    }
    return m
  }, [hanHexes, hanDeleteSet, pendingClanMap, clanIndex])

  /**
   * Fronteras de Han como paths continuos (silueta Han↔Han / Han↔vacío).
   * Sustituye el antiguo stroke=hanColor en cada hex, que sellaba juntas pero
   * dibujaba bordes internos y se confundía con clanes.
   */
  const hanBorderPaths = useMemo(() => {
    /** @type {Map<string, string>} key "q,r" → "han:slug" */
    const ownerByKey = new Map()
    /** @type {Map<string, string>} owner → stroke color */
    const strokeByOwner = new Map()

    const hanKeyOf = (c) => {
      const slug = String(c?.hanSlug || c?.territorySlug || '').trim().toLowerCase()
      if (slug) return `han:${slug}`
      if (c?.hanId != null) return `hanid:${c.hanId}`
      return null
    }

    const colorOf = (c) => {
      if (typeof c?.hanColor === 'string' && /^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(c.hanColor)) {
        return stripAlphaHex(c.hanColor)
      }
      const fromPal =
        hanPalette?.byId?.[String(c?.hanId)]
        || hanPalette?.byId?.[String(c?.id)]
        || hanPalette?.bySlug?.[String(c?.hanSlug)]
        || hanPalette?.bySlug?.[String(c?.territorySlug)]
        || null
      if (fromPal) return stripAlphaHex(fromPal)
      return stripAlphaHex(colorForCell(c)?.fill) || '#888888'
    }

    for (const { c, q, r } of hanHexes) {
      const key = toKey(q, r)
      if (hanDeleteSet.has(key)) continue
      const owner = hanKeyOf(c)
      if (!owner) continue
      ownerByKey.set(key, owner)
      if (!strokeByOwner.has(owner)) strokeByOwner.set(owner, colorOf(c))
    }

    /** @type {Map<string, { stroke: string, edges: Array<{a:[number,number], b:[number,number]}> }>} */
    const byOwner = new Map()
    for (const { q, r, x, y } of hanHexes) {
      const key = toKey(q, r)
      const ownerKey = ownerByKey.get(key)
      if (!ownerKey) continue
      if (!byOwner.has(ownerKey)) {
        byOwner.set(ownerKey, { stroke: strokeByOwner.get(ownerKey) || '#888', edges: [] })
      }
      const bucket = byOwner.get(ownerKey)
      for (let dir = 0; dir < 6; dir++) {
        const [nq, nr] = axialNeighbor(q, r, dir)
        const nOwner = ownerByKey.get(toKey(nq, nr))
        if (nOwner === ownerKey) continue
        if (nOwner && String(ownerKey) >= String(nOwner)) continue
        const [a, b] = hexEdgeEndpoints(x, y, hexSize, AXIAL_DIR_TO_EDGE[dir])
        bucket.edges.push({ a, b })
      }
    }

    const focusSlug =
      typeof hanFocusSlug === 'string' && hanFocusSlug.trim()
        ? hanFocusSlug.trim().toLowerCase()
        : null

    const out = []
    for (const [owner, { stroke, edges }] of byOwner) {
      const ownerSlug = String(owner).startsWith('han:')
        ? String(owner).slice(4)
        : String(owner).replace(/^hanid:/, '')
      // Con Han en foco: solo la silueta de ese Han (el resto parecía un “grid” del mapa).
      if (focusSlug && owner !== `han:${focusSlug}` && ownerSlug !== focusSlug) continue
      for (const d of edgesToSvgPathD(edges)) {
        out.push({ owner, stroke, d, opacity: 1 })
      }
    }
    return out
  }, [hanHexes, hanDeleteSet, hanPalette, hexSize, hanFocusSlug])

  /**
   * Trazos de clan: malla completa (aristas internas + frontera) en el color del clan.
   * Así las celdas internas comparten el mismo color que la silueta exterior.
   */
  const clanBorderPaths = useMemo(() => {
    /** @type {Map<string, { stroke: string, edges: Array<{a:[number,number], b:[number,number]}> }>} */
    const byOwner = new Map()

    for (const { q, r, x, y } of hanHexes) {
      const key = toKey(q, r)
      // Con Han en foco, no dibujar mallas de clan fuera de ese territorio
      if (focusedHanKeySet && !focusedHanKeySet.has(key)) continue
      const ownerKey = borderOwnerByKey.get(key)
      if (!ownerKey) continue

      const pendingAssign = pendingClanMap.get(key)
      const baseClan = clanIndex.get(key)
      const effClanId = pendingAssign?.clanId != null ? pendingAssign.clanId : (baseClan?.clanId ?? null)
      const effClanColor = pendingAssign?.clanColor != null ? pendingAssign.clanColor : (baseClan?.clanColor ?? null)
      const stroke = effClanColor || (effClanId != null ? hashClanColor(effClanId) : null)
      if (!stroke) continue

      if (!byOwner.has(ownerKey)) byOwner.set(ownerKey, { stroke, edges: [] })
      const bucket = byOwner.get(ownerKey)

      for (let dir = 0; dir < 6; dir++) {
        const [nq, nr] = axialNeighbor(q, r, dir)
        const nKey = toKey(nq, nr)
        const nOwner = borderOwnerByKey.get(nKey)

        if (nOwner === ownerKey) {
          // Arista interna clan↔clan (mismo dueño): una sola vez
          if (key > nKey) continue
        } else if (nOwner && String(nOwner).startsWith('clan:') && String(ownerKey) >= String(nOwner)) {
          // Frontera entre dos clanes distintos: una sola vez
          continue
        }

        const [a, b] = hexEdgeEndpoints(x, y, hexSize, AXIAL_DIR_TO_EDGE[dir])
        bucket.edges.push({ a, b })
      }
    }

    const out = []
    for (const [owner, { stroke, edges }] of byOwner) {
      for (const d of edgesToSvgPathD(edges)) {
        out.push({ owner, stroke, d })
      }
    }
    return out
  }, [hanHexes, borderOwnerByKey, pendingClanMap, clanIndex, hexSize, focusedHanKeySet])

  const empireDeleteSet = useMemo(() => {
    const s = new Set()
    const list = Array.isArray(pendingDeletes) ? pendingDeletes : []
    for (const it of list) if (it.layer === 'empire') s.add(toKey(it.q, it.r))
    return s
  }, [pendingDeletes])

  // Grid overlay opcional.
  // Malla hexagonal completa. Con hanFocusSlug: solo celdas de ese Han.
  const gridLines = useMemo(() => {
    if (!showGrid) return []
    const scope = focusedHanKeySet
      ? hanHexes.filter(({ q, r }) => focusedHanKeySet.has(toKey(q, r)))
      : hanHexes
    if (scope.length === 0) return []

    const inScope = new Set(scope.map(({ q, r }) => toKey(q, r)))
    const outlines = []
    const seen = new Set()

    for (const h of scope) {
      const { x, y } = axialToPixel(h.q, h.r, hexSize)
      for (let dir = 0; dir < 6; dir++) {
        const [nq, nr] = axialNeighbor(h.q, h.r, dir)
        const nKey = toKey(nq, nr)
        const neighborInScope = inScope.has(nKey)

        if (neighborInScope) {
          const a = toKey(h.q, h.r)
          const edgeId = a < nKey ? `${a}|${nKey}` : `${nKey}|${a}`
          if (seen.has(edgeId)) continue
          seen.add(edgeId)
        }

        const [pa, pb] = hexEdgeEndpoints(x, y, hexSize, AXIAL_DIR_TO_EDGE[dir])
        outlines.push({ a: pa, b: pb })
      }
    }

    return outlines
  }, [hanHexes, hexSize, showGrid, focusedHanKeySet])

  // Color del grid según luminancia del fondo (Han en foco o muestra del mapa).
  const gridStroke = useMemo(() => {
    if (!showGrid) return '#ffffff66'
    const scope = focusedHanKeySet
      ? hanHexes.filter(({ q, r }) => focusedHanKeySet.has(toKey(q, r)))
      : hanHexes
    if (!scope.length) return '#ffffff66'

    let rSum = 0
    let gSum = 0
    let bSum = 0
    let n = 0
    const sample = scope.length > 40 ? scope.filter((_, i) => i % Math.ceil(scope.length / 40) === 0) : scope
    for (const { c } of sample) {
      const raw =
        (typeof c?.hanColor === 'string' && c.hanColor)
        || hanPalette?.byId?.[String(c?.hanId)]
        || hanPalette?.byId?.[String(c?.id)]
        || hanPalette?.bySlug?.[String(c?.hanSlug)]
        || hanPalette?.bySlug?.[String(c?.territorySlug)]
        || null
      const hex = stripAlphaHex(raw)
      const m = typeof hex === 'string' && hex.match(/^#([0-9a-f]{6})$/i)
      if (!m) continue
      rSum += parseInt(m[1].slice(0, 2), 16)
      gSum += parseInt(m[1].slice(2, 4), 16)
      bSum += parseInt(m[1].slice(4, 6), 16)
      n += 1
    }
    if (!n) return '#ffffff66'
    const avg = `#${[rSum, gSum, bSum]
      .map((v) => Math.round(v / n).toString(16).padStart(2, '0'))
      .join('')}`
    return gridStrokeForBackground(avg)
  }, [showGrid, hanHexes, focusedHanKeySet, hanPalette])

  const handleCellClick = (cell) => {
    onCellClick?.(cell)
  }

  // Eventos de edición legacy (sobre hex directo)
  const startPaint = (cell, ev) => {
    if (!editable || toolMode === 'move') return
    if (ev && ev.cancelable) ev.preventDefault()
    ev?.stopPropagation?.()
    setIsPainting(true)
    onBrushStart?.(cell)
    onHoverCell?.(cell)
  }
  const overPaint = (cell, ev) => {
    if (!editable || toolMode === 'move') return
    if (ev && ev.cancelable) ev.preventDefault()
    ev?.stopPropagation?.()
    if (isPainting) {
      onBrushOver?.(cell)
    }
    onHoverCell?.(cell)
  }
  const endPaint = (ev) => {
    if (!editable) return
    if (ev && ev.cancelable) ev.preventDefault()
    ev?.stopPropagation?.()
    setIsPainting(false)
    onBrushEnd?.()
    onHoverCell?.(null)
  }

  return (
    <div
      className="hexmap-container"
      ref={containerRef}
      onKeyDown={onKeyDown}
      role="application"
      aria-label="Mapa de territorios en celdas hexagonales"
      tabIndex={0}
      style={containerStyle}
    >
      <svg
        className="hexmap-svg"
        ref={svgRef}
        onWheel={onWheel}
        onMouseDown={handleSvgMouseDown}
        onMouseMove={handleSvgMouseMove}
        onMouseUp={(e) => {
          endPan()
          endPaint(e)
        }}
        onMouseLeave={(e) => {
          endPan()
          endPaint(e)
        }}
      >
        <g transform={`translate(${translate.x},${translate.y}) scale(${scale})`}>
          {/* Fondo global (imagen) — ocupa el bbox de las celdas y va detrás de todo */}
          {(() => {
            const hasUrl = typeof backgroundImageUrl === 'string' && backgroundImageUrl.trim().length > 0
            const w = renderBounds.maxX - renderBounds.minX
            const h = renderBounds.maxY - renderBounds.minY
            const validGeom = Number.isFinite(w) && Number.isFinite(h) && (w > 0 || h > 0)
            if (!hasUrl || !validGeom) return null
            const opacity = Math.max(0, Math.min(1, Number(backgroundOpacity ?? 1)))
            return (
              <image
                href={backgroundImageUrl}
                x={renderBounds.minX}
                y={renderBounds.minY}
                width={Math.max(1, w)}
                height={Math.max(1, h)}
                preserveAspectRatio="none"
                opacity={opacity}
                style={{ pointerEvents: 'none', imageRendering: 'crisp-edges' }}
              />
            )
          })()}
          {/* Capa Imperio (fondo, fill prioritario) */}
          {showEmpire &&
            empireHexes.map(({ c, q, r, pts }, idx) => {
              const colors = colorForCell(c)
              const fill = colors.fill
              // Foco de imperio: si se provee empireFocusSlug, atenuar otros imperios
              const focusSlug = typeof empireFocusSlug === 'string' && empireFocusSlug.trim()
                ? empireFocusSlug.trim().toLowerCase()
                : null
              const thisSlug = String(
                c?.empireSlug ||
                c?.shogunateSlug ||
                c?.territoryEmpireSlug ||
                c?.empireName ||
                c?.shogunateName ||
                ''
              ).toLowerCase()
              const dim = Math.max(0, Math.min(1, Number(empireDimOpacity) || 0.25))
              let fillOpacity = focusSlug
                ? (thisSlug === focusSlug ? 1 : dim)
                : (showHan && activeLayer !== 'empire' ? 0.35 : 1)
              // Con Han en foco: el imperio bajo los vecinos no debe “aplanar” el contraste
              const inFocusedHan = !focusedHanKeySet || focusedHanKeySet.has(toKey(q, r))
              if (focusedHanKeySet) {
                fillOpacity = inFocusedHan
                  ? Math.min(fillOpacity, 0.45)
                  : Math.min(fillOpacity, dim)
              }
              // Sellado solo dentro del Han en foco (fuera, opacity baja → malla fantasma)
              const stroke = (fill && fill !== 'none' && inFocusedHan) ? fill : 'none'

              return (
                <g key={`emp-${c.territorySlug || 't'}-${q},${r}-${idx}`}>
                  <polygon
                    className="hexmap-hex hexmap-hex--empire"
                    points={pts}
                    fill={fill}
                    fillOpacity={fillOpacity}
                    stroke={stroke}
                    strokeWidth={stroke === 'none' ? 0 : 1}
                    strokeOpacity={fillOpacity}
                    strokeLinejoin="round"
                    vectorEffect={stroke === 'none' ? undefined : 'non-scaling-stroke'}
                    onMouseEnter={(e) => {
                      const empLabel =
                        c?.empireName ||
                        c?.empireSlug ||
                        c?.territoryEmpireName ||
                        c?.territoryEmpireSlug ||
                        c?.shogunateName ||
                        c?.shogunateSlug ||
                        'Imperio'
                      setHoverCell({ ...c, __layer: 'empire', __empireLabel: empLabel })
                      if (editable) overPointer(e)
                      try {
                        console.debug?.('[HexMap] empire:hover', {
                          q,
                          r,
                          territory: c?.territorySlug,
                          empire: empLabel,
                        })
                      } catch { }
                    }}
                    onMouseLeave={() => setHoverCell(null)}
                    onClick={() => handleCellClick({ ...c, __layer: 'empire' })}
                    onMouseDown={(e) => {
                      if (editable && toolMode !== 'move') startPaintAtPointer(e)
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleCellClick({ ...c, __layer: 'empire' })
                      }
                    }}
                    aria-label={`${c.empireName || c.empireSlug || c.territoryEmpireName || c.territoryEmpireSlug || c.shogunateName || c.shogunateSlug || 'Imperio'} · (${q},${r})`}
                  />
                </g>
              )
            })}

          {/* Capa Han (frente): fill si no hay Imperio, stroke por clan/estado */}
          {showHan &&
            hanHexes.map(({ c, q, r, x, y, pts }, idx) => {
              const key = toKey(q, r)

              // Fill Han: usar color del Han (hanColor) si viene del backend; fallback por estado.
              // Si existe Imperio en la celda y está visible (empireKeySet), no se pinta (prioridad Imperio).
              const statusColors = getStatusColors(c.status)
              const colorsHan = colorForCell(c)
              const baseHanFill = colorsHan?.fill || statusColors?.fill || 'transparent'
              // Preferir el color definido del Han (backend/catálogo), no uno arbitrario
              const definedHanHex =
                (typeof c?.hanColor === 'string' && /^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(c.hanColor))
                  ? c.hanColor
                  : (
                    hanPalette?.byId?.[String(c?.hanId)]
                    || hanPalette?.byId?.[String(c?.id)]
                    || hanPalette?.bySlug?.[String(c?.hanSlug)]
                    || hanPalette?.bySlug?.[String(c?.territorySlug)]
                    || null
                  )

              let fill
              // Cuando la capa activa es Han o los Imperios están ocultos, usamos siempre el color definido del Han como fill.
              if (activeLayer === 'han' || !showEmpire) {
                const chosen = definedHanHex || baseHanFill
                fill = stripAlphaHex(chosen)
              } else {
                // Comportamiento normal: si Imperio está visible en la celda, no rellenar Han
                fill = empireKeySet.has(key) ? 'none' : baseHanFill
              }

              // Compatibilidad legacy: si pendingIndex marca erase (Neutral sin clan), mostrar fill none y stroke rojo punteado
              const pendLegacy = pendingIndex.get(key)
              const isLegacyErase = !!(pendLegacy && String(pendLegacy.status) === 'Neutral' && (pendLegacy.clanId == null))
              if (isLegacyErase) {
                fill = 'none'
              }

              // Clan: solo marca CSS; el borde visible va en clanBorderPaths.
              // Sellado de juntas: stroke = fill (antes lo cubría el borde hanColor/clan por hex).
              let hasClanStroke = false
              let isHanDeletePreview = false
              let deleteStroke = null
              if (!hanDeleteSet.has(key)) {
                const pendingAssign = pendingClanMap.get(key)
                const hasPendingClear =
                  !!(pendingAssign && Object.prototype.hasOwnProperty.call(pendingAssign, 'clanId') && pendingAssign.clanId === null)

                if (!hasPendingClear) {
                  const baseClan = clanIndex.get(key)
                  const effClanId = (pendingAssign?.clanId != null) ? pendingAssign.clanId : (baseClan?.clanId ?? null)
                  if (effClanId != null) {
                    hasClanStroke = true
                  }
                }
              } else {
                isHanDeletePreview = true
                deleteStroke = statusColors?.stroke || '#999'
              }

              const strokeDasharray = isLegacyErase ? '6 4' : undefined

              // Atenuación de Han vecinos: si hay hanFocusSlug, atenuar los Han cuyo slug no coincide
              const focusSlug =
                typeof hanFocusSlug === 'string' && hanFocusSlug.trim() ? hanFocusSlug.trim().toLowerCase() : null
              const thisHanSlug = String(
                c?.hanSlug ||
                c?.territorySlug ||
                c?.hanName ||
                c?.territoryName ||
                ''
              ).toLowerCase()
              const hanDim = Math.max(0, Math.min(1, Number(hanDimOpacity) || 0.35))
              let hanFillOpacity = focusSlug ? (thisHanSlug === focusSlug ? 1 : hanDim) : 1
              // Sin Han en foco pero con imperio: atenuar Hans fuera de ese imperio
              if (!focusSlug && typeof empireFocusSlug === 'string' && empireFocusSlug.trim()) {
                const empFocus = empireFocusSlug.trim().toLowerCase()
                const cellEmp = String(
                  c?.empireSlug ||
                  c?.shogunateSlug ||
                  empireSlugByKey.get(key) ||
                  ''
                ).toLowerCase()
                if (cellEmp && cellEmp !== empFocus) hanFillOpacity = hanDim
              }
              const strokeOp = isLegacyErase
                ? 1
                : (fill !== 'none' || isHanDeletePreview ? hanFillOpacity : 0)

              // Con Han en foco, el sellado en vecinos atenuados (opacity baja) deja ver el
              // fondo entre hexes → parece una malla en todo el mapa menos el Han actual.
              const isFocusedHanCell = !focusedHanKeySet || focusedHanKeySet.has(key)
              const seamSeal =
                !isLegacyErase && !isHanDeletePreview && fill !== 'none' && isFocusedHanCell
              const polyStroke = isLegacyErase
                ? '#FF5252'
                : (isHanDeletePreview ? deleteStroke : (seamSeal ? fill : 'none'))
              const polyStrokeWidth = isLegacyErase || isHanDeletePreview || seamSeal ? 1 : 0

              return (
                <g key={`${c.territorySlug || 'h'}-${q},${r}-${idx}`} className={hasClanStroke ? 'hexmap-hex-clan-border' : undefined}>
                  <polygon
                    className={`hexmap-hex${editable ? ' hexmap-hex--editable' : ''}${hasClanStroke ? ' hexmap-hex--has-clan' : ''}`}
                    points={pts}
                    pointerEvents="all"
                    fill={fill}
                    fillOpacity={fill === 'none' ? 1 : hanFillOpacity}
                    stroke={polyStroke}
                    strokeDasharray={strokeDasharray}
                    strokeWidth={polyStrokeWidth}
                    strokeOpacity={strokeOp}
                    strokeLinejoin="round"
                    vectorEffect={seamSeal ? 'non-scaling-stroke' : undefined}
                    onMouseEnter={(e) => {
                      // Resolver clan efectivo para tooltip (pendiente > base clan > celda)
                      const pendingAssign = pendingClanMap.get(key)
                      const hasPendingClear =
                        !!(pendingAssign && Object.prototype.hasOwnProperty.call(pendingAssign, 'clanId') && pendingAssign.clanId === null)
                      let effClanId = null
                      if (!hasPendingClear) {
                        const baseClan = clanIndex.get(key)
                        if (pendingAssign && pendingAssign.clanId != null) effClanId = pendingAssign.clanId
                        else if (baseClan && baseClan.clanId != null) effClanId = baseClan.clanId
                        else if (c && c.clanId != null) effClanId = c.clanId
                      }
                      let clanLabel = c?.clanName || c?.clanSlug || null
                      if (!clanLabel && effClanId != null) {
                        const fromDict = clanDict && clanDict[String(effClanId)]
                        clanLabel = fromDict || c?.clanName || c?.clanSlug || (effClanId != null ? `Clan ${effClanId}` : '—')
                      }
                      setHoverCell({ ...c, __layer: 'han', __clanName: clanLabel, __clanId: effClanId })
                      if (editable) overPointer(e)
                    }}
                    onMouseLeave={() => setHoverCell(null)}
                    onClick={() => {
                      // Enriquecer celda con clan efectivo para el panel
                      const key = toKey(q, r)
                      const pendingAssign = pendingClanMap.get(key)
                      const hasPendingClear =
                        !!(pendingAssign && Object.prototype.hasOwnProperty.call(pendingAssign, 'clanId') && pendingAssign.clanId === null)
                      let effClanId = null
                      if (!hasPendingClear) {
                        const baseClan = clanIndex.get(key)
                        if (pendingAssign && pendingAssign.clanId != null) effClanId = pendingAssign.clanId
                        else if (baseClan && baseClan.clanId != null) effClanId = baseClan.clanId
                        else if (c && c.clanId != null) effClanId = c.clanId
                      }
                      let effClanName = c?.clanName || c?.clanSlug || null
                      if (!effClanName && effClanId != null) {
                        const fromDict = clanDict && clanDict[String(effClanId)]
                        effClanName = fromDict || c?.clanName || c?.clanSlug || (effClanId != null ? `Clan ${effClanId}` : '—')
                      }
                      handleCellClick({ ...c, __layer: 'han', __clanId: effClanId, __clanName: effClanName })
                    }}
                    onMouseDown={(e) => {
                      if (editable && toolMode !== 'move') startPaintAtPointer(e)
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        // Igual que onClick, con enriquecimiento de clan
                        const key = toKey(q, r)
                        const pendingAssign = pendingClanMap.get(key)
                        const hasPendingClear =
                          !!(pendingAssign && Object.prototype.hasOwnProperty.call(pendingAssign, 'clanId') && pendingAssign.clanId === null)
                        let effClanId = null
                        if (!hasPendingClear) {
                          const baseClan = clanIndex.get(key)
                          if (pendingAssign && pendingAssign.clanId != null) effClanId = pendingAssign.clanId
                          else if (baseClan && baseClan.clanId != null) effClanId = baseClan.clanId
                          else if (c && c.clanId != null) effClanId = c.clanId
                        }
                        let effClanName = c?.clanName || c?.clanSlug || null
                        if (!effClanName && effClanId != null) {
                          const fromDict = clanDict && clanDict[String(effClanId)]
                          effClanName = fromDict || c?.clanName || c?.clanSlug || (effClanId != null ? `Clan ${effClanId}` : '—')
                        }
                        handleCellClick({ ...c, __layer: 'han', __clanId: effClanId, __clanName: effClanName })
                      }
                    }}
                    aria-label={`${c.territoryName || c.territorySlug} (${q},${r}) - ${c.status}${c.clanName ? ' - ' + c.clanName : ''}`}
                  />
                  {showStatusTint && isFocusedHanCell && fill !== 'none' && !isLegacyErase && (() => {
                    const statusKey = normalizeStatus(c.status)
                    const tint = stripAlphaHex(statusColors?.stroke || getStatusColors(statusKey).stroke)
                    if (!tint || tint === 'none') return null
                    const hot = statusKey === 'Guerra' || statusKey === 'Tension' || statusKey === 'Disputa'
                    const baseOp = hot ? 0.2 : 0.12
                    return (
                      <polygon
                        key={`status-tint-${q},${r}`}
                        className="hexmap-hex-status-tint"
                        points={pts}
                        fill={tint}
                        fillOpacity={baseOp * hanFillOpacity}
                        stroke={hot ? tint : 'none'}
                        strokeWidth={hot ? 1 : 0}
                        strokeOpacity={hot ? 0.4 * hanFillOpacity : 0}
                        strokeLinejoin="round"
                        vectorEffect={hot ? 'non-scaling-stroke' : undefined}
                        pointerEvents="none"
                      />
                    )
                  })()}
                </g>
              )
            })}

          {/* Grid debajo de fronteras Han/clan para no tapar el color de clan */}
          {showGrid &&
            gridLines.map((ln, i) => (
              <line
                key={`grid-${i}`}
                x1={ln.a[0]}
                y1={ln.a[1]}
                x2={ln.b[0]}
                y2={ln.b[1]}
                stroke={gridStroke}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            ))}

          {/* Bordes de Han: path continuo por silueta (Han↔Han / exterior) */}
          {showHan &&
            hanBorderPaths.map(({ owner, stroke, d, opacity }, idx) => (
              <path
                key={`han-path-${owner}-${idx}`}
                className="hexmap-han-edge"
                d={d}
                fill="none"
                stroke={stroke}
                strokeWidth={1.5}
                strokeLinecap="butt"
                strokeLinejoin="round"
                strokeOpacity={opacity}
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
                pointerEvents="none"
              />
            ))}

          {/* Bordes de clan: malla interna + frontera (mismo color) */}
          {clanBorderPaths.map(({ owner, stroke, d }, idx) => (
            <path
              key={`clan-path-${owner}-${idx}`}
              className="hexmap-clan-edge"
              d={d}
              fill="none"
              stroke={stroke}
              strokeWidth={2}
              strokeLinecap="butt"
              strokeLinejoin="round"
              strokeOpacity={1}
              vectorEffect="non-scaling-stroke"
              shapeRendering="geometricPrecision"
              pointerEvents="none"
            />
          ))}

          {/* Overlays de edición legacy (pendingCells) */}
          {editable &&
            overlayHexes.map(({ c, pts, colors }, idx) => {
              const isErase = String(c?.status) === 'Neutral' && (c?.clanId == null)
              const stroke = isErase ? '#FF5252' : colors.stroke
              const fill = isErase ? 'none' : colors.fill
              const dash = isErase ? '6 4' : undefined
              const opacity = isErase ? 0 : 0.85
              return (
                <g key={`ovl-${c.territorySlug || 't'}-${c.q},${c.r}-${idx}`}>
                  <polygon
                    className="hexmap-hex hexmap-hex--pending"
                    points={pts}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={2}
                    fillOpacity={opacity}
                    strokeDasharray={dash}
                    onMouseEnter={(e) => {
                      setHoverCell(c)
                      overPointer(e)
                    }}
                    onMouseLeave={() => setHoverCell(null)}
                    onMouseDown={(e) => {
                      if (editable && toolMode !== 'move') startPaintAtPointer(e)
                    }}
                  />
                </g>
              )
            })}

          {/* Overlays nuevos: pendingEmpireCells (fill alpha ~0.6) */}
          {Array.isArray(pendingEmpireOverlay) &&
            pendingEmpireOverlay.map(({ c, q, r, pts }, idx) => {
              const colors = colorForCell(c)
              const fill = colors.fill
              return (
                <g key={`ovl-emp-${c.territorySlug || 't'}-${q},${r}-${idx}`}>
                  <polygon
                    className="hexmap-hex hexmap-hex--pending-empire"
                    points={pts}
                    fill={fill}
                    fillOpacity={0.6}
                    stroke="none"
                  />
                </g>
              )
            })}

          {/* Overlays nuevos: pendingHanCells (fill alpha ~0.6) */}
          {Array.isArray(pendingHanOverlay) &&
            pendingHanOverlay.map(({ c, q, r, pts }, idx) => {
              const statusColors = getStatusColors(c.status)
              const fill = statusColors.fill
              return (
                <g key={`ovl-han-${c.territorySlug || 't'}-${q},${r}-${idx}`}>
                  <polygon
                    className="hexmap-hex hexmap-hex--pending-han"
                    points={pts}
                    fill={fill}
                    fillOpacity={0.6}
                    stroke="none"
                  />
                </g>
              )
            })}

          {/* Overlays nuevos: pendingClanAssignments (stroke-only)
              Nota: si clanId === null significa "limpiar clan" (erase-clan).
              En ese caso NO dibujamos overlay, para que se vea el borde por estado
              (y no un gris por defecto). */}
          {Array.isArray(pendingClanAssignments) &&
            (() => {
              /** @type {Map<string, { stroke: string, edges: Array<{a:[number,number], b:[number,number]}> }>} */
              const orphanByOwner = new Map()
              for (const it of pendingClanAssignments) {
                if (it?.clanId == null) continue
                const q = Number(it.q)
                const r = Number(it.r)
                const key = toKey(q, r)
                // La capa continua ya pinta celdas en borderOwnerByKey
                if (borderOwnerByKey.has(key)) continue
                const ownerKey = `clan:${it.clanId}`
                const stroke = it?.clanColor || hashClanColor(it.clanId)
                if (!orphanByOwner.has(ownerKey)) orphanByOwner.set(ownerKey, { stroke, edges: [] })
                const { x, y } = axialToPixel(q, r, hexSize)
                for (let dir = 0; dir < 6; dir++) {
                  const [nq, nr] = axialNeighbor(q, r, dir)
                  const nOwner = borderOwnerByKey.get(toKey(nq, nr))
                  if (nOwner === ownerKey) continue
                  const [a, b] = hexEdgeEndpoints(x, y, hexSize, AXIAL_DIR_TO_EDGE[dir])
                  orphanByOwner.get(ownerKey).edges.push({ a, b })
                }
              }
              const paths = []
              for (const [owner, { stroke, edges }] of orphanByOwner) {
                for (const d of edgesToSvgPathD(edges)) {
                  paths.push({ owner, stroke, d })
                }
              }
              return paths.map(({ owner, stroke, d }, idx) => (
                <path
                  key={`ovl-clan-path-${owner}-${idx}`}
                  className="hexmap-clan-edge"
                  d={d}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={2}
                  strokeLinecap="butt"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  shapeRendering="geometricPrecision"
                  pointerEvents="none"
                />
              ))
            })()}

          {/* Overlays nuevos: pendingDeletes (fill none + stroke rojo punteado) */}
          {Array.isArray(pendingDeletes) &&
            pendingDeletes.map((it, idx) => {
              const { q, r } = it
              const { x, y } = axialToPixel(Number(q), Number(r), hexSize)
              const pts = toPointsAttr(hexPolygonPoints(x, y, hexSize))
              return (
                <g key={`ovl-del-${it.layer}-${it.territorySlug || 't'}-${q},${r}-${idx}`}>
                  <polygon
                    className="hexmap-hex hexmap-hex--pending-delete"
                    points={pts}
                    fill="none"
                    stroke="#FF5252"
                    strokeDasharray="6 4"
                    strokeWidth={2}
                  />
                </g>
              )
            })}

          {/* Resaltado sutil de la celda destacada (sin fill, con halo + trazo) */}
          {highlightHex &&
            Number.isFinite(Number(highlightHex?.q)) &&
            Number.isFinite(Number(highlightHex?.r)) && (() => {
              const q = Number(highlightHex.q)
              const r = Number(highlightHex.r)
              const { x, y } = axialToPixel(q, r, hexSize)
              const pts = toPointsAttr(hexPolygonPoints(x, y, hexSize))
              const haloWidth = Math.max(1, Number(highlightStrokeWidth) + 2)
              return (
                <g key={`hl-${q},${r}`}>
                  {/* Halo sutil */}
                  <polygon
                    points={pts}
                    fill="none"
                    stroke="#00000066"
                    strokeWidth={haloWidth}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {/* Trazo principal */}
                  <polygon
                    points={pts}
                    fill="none"
                    stroke={highlightColor || '#FFD54F'}
                    strokeWidth={Number(highlightStrokeWidth) || 2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                </g>
              )
            })()}

          {/* Marcadores (ej. Capital) */}
          {Array.isArray(markers) &&
            markers.map((m, idx) => {
              const q = Number(m?.q)
              const r = Number(m?.r)
              if (!Number.isFinite(q) || !Number.isFinite(r)) return null
              const { x, y } = axialToPixel(q, r, hexSize)
              const icon = m?.icon || '🏯'
              const fontSize = Math.max(10, hexSize) // escala con el tamaño del hex
              const key = toKey(q, r)
              const empireLabel =
                empireLabelByKey.get(key) ||
                m?.empireLabel ||
                'Imperio'
              return (
                <g
                  key={`marker-${q},${r}-${idx}`}
                  pointerEvents="auto"
                  role="button"
                  tabIndex={0}
                  onMouseEnter={(e) => {
                    const mk = { ...m, q, r, empireLabel }
                    setHoverMarker(mk)
                    onMarkerHover?.(mk)
                  }}
                  onMouseLeave={() => {
                    setHoverMarker(null)
                    onMarkerLeave?.(m)
                  }}
                  onMouseDown={(e) => {
                    // Evitar iniciar pan/pintado al pulsar sobre el marcador
                    if (e && e.cancelable) e.preventDefault()
                    e?.stopPropagation?.()
                  }}
                  onClick={(e) => {
                    e?.stopPropagation?.()
                    const mk = { ...m, q, r, empireLabel }
                    onMarkerClick?.(mk)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      const mk = { ...m, q, r, empireLabel }
                      onMarkerClick?.(mk)
                    }
                  }}
                >
                  {/* Área de hit opcional (transparente) */}
                  <circle cx={x} cy={y} r={Math.max(10, hexSize * 0.6)} fill="transparent" />
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fontSize}
                  >
                    {icon}
                  </text>
                </g>
              )
            })}

        </g>
      </svg>

      {/* Tooltip flotante */}
      {hoverMarker ? (
        <div className="hexmap-tooltip" role="tooltip">
          <div className="hexmap-tooltip__title">
            Capital: {hoverMarker.name || '—'}
          </div>
          <div className="hexmap-tooltip__row">
            <span className="hexmap-tooltip__label">Imperio:</span> {hoverMarker.empireLabel || 'Imperio'}
          </div>
          <div className="hexmap-tooltip__row hexmap-tooltip__muted">
            ({hoverMarker.q},{hoverMarker.r})
          </div>
        </div>
      ) : (
        hoverCell && (
          <div className="hexmap-tooltip" role="tooltip">
            <div className="hexmap-tooltip__title">
              {(() => {
                const hasCoords =
                  Number.isFinite(Number(hoverCell?.q)) &&
                  Number.isFinite(Number(hoverCell?.r))
                const key = hasCoords ? toKey(hoverCell.q, hoverCell.r) : null
                const empireLabel = (key && empireLabelByKey.get(key)) || 'Imperio'
                const hanLabel = hoverCell.hanName || hoverCell.hanSlug || hoverCell.territoryName || hoverCell.territorySlug || '—'
                const isEmpire = hoverCell?.__layer === 'empire'
                return isEmpire ? `Imperio: ${empireLabel}` : `Han: ${hanLabel}`
              })()}
            </div>


            <div className="hexmap-tooltip__row">
              <span className="hexmap-tooltip__label">Estado:</span> {hoverCell.status}
            </div>

            {hoverCell.__layer !== 'empire' && (() => {
              // Cálculo robusto del clan efectivo también en tooltip
              const hasCoords =
                Number.isFinite(Number(hoverCell?.q)) &&
                Number.isFinite(Number(hoverCell?.r))
              let label = hoverCell?.__clanName || hoverCell?.clanName || hoverCell?.clanSlug || null
              if (!label && hasCoords) {
                const key = toKey(Number(hoverCell.q), Number(hoverCell.r))
                const baseClan = clanIndex.get(key)
                const effId = baseClan?.clanId != null ? baseClan.clanId : (hoverCell?.clanId != null ? hoverCell.clanId : null)
                if (effId != null) {
                  const fromDict = clanDict && clanDict[String(effId)]
                  label = fromDict || baseClan?.clanName || baseClan?.clanSlug || (hoverCell?.clanName || hoverCell?.clanSlug) || `Clan ${effId}`
                }
              }
              return (
                <div className="hexmap-tooltip__row">
                  <span className="hexmap-tooltip__label">Clan:</span> {label || '—'}
                </div>
              )
            })()}

            <div className="hexmap-tooltip__row hexmap-tooltip__muted">
              ({hoverCell.q},{hoverCell.r}) · {hoverCell.since ? new Date(hoverCell.since).toLocaleDateString() : 's/f'}
            </div>
          </div>
        )
      )}
    </div>
  )
}